import { Doughnut } from "react-chartjs-2";
import "./chartSetup";

export interface PieChartDatum {
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieChartDatum[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  emptyLabel?: string;
}

const DONUT_BACKGROUND = "#E4EAF0";
const CENTER_VALUE_COLOR = "#10253A";
const CENTER_LABEL_COLOR = "#63788E";

const donutPlugin = {
  id: "diagnomiqDonutCustom",
  beforeDatasetsDraw: (chart: any) => {
    const arc = chart.getDatasetMeta(0)?.data?.[0];
    if (!arc) return;
    const { x, y, innerRadius, outerRadius } = arc;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, outerRadius, 0, Math.PI * 2);
    ctx.arc(x, y, innerRadius, 0, Math.PI * 2, true);
    ctx.fillStyle = DONUT_BACKGROUND;
    ctx.fill();
    ctx.restore();
  },
  afterDatasetsDraw: (chart: any) => {
    const opts = chart.config.options?.plugins?.diagnomiqDonutCustom;
    if (!opts) return;
    const arc = chart.getDatasetMeta(0)?.data?.[0];
    if (!arc) return;
    const { x, y } = arc;
    const { total, label, size } = opts;
    const valueFont = Math.round(size * 0.16);
    const labelFont = Math.round(size * 0.085);
    const ctx = chart.ctx;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = CENTER_VALUE_COLOR;
    ctx.font = `700 ${valueFont}px Arial, sans-serif`;
    ctx.fillText(String(total), x, y - Math.round(size * 0.02));
    ctx.fillStyle = CENTER_LABEL_COLOR;
    ctx.font = `400 ${labelFont}px Arial, sans-serif`;
    ctx.fillText(label, x, y + Math.round(size * 0.1));
    ctx.restore();
  },
};

const PieChart = ({
  data,
  size = 180,
  thickness = 26,
  centerLabel = "Total",
  emptyLabel = "No data",
}: PieChartProps) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const radius = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;

  if (total === 0) {
    return (
      <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          role="img"
          aria-label={emptyLabel}
        >
          <title>{emptyLabel}</title>
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={thickness}
          />
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" className="fill-gray-400 text-sm">
            {emptyLabel}
          </text>
        </svg>
      </div>
    );
  }

  const chartData = {
    labels: data.map((d) => d.label),
    datasets: [
      {
        data: data.map((d) => d.value),
        backgroundColor: data.map((d) => d.color),
        borderColor: "transparent",
        borderWidth: 0,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    cutout: `${Math.round((1 - (2 * thickness) / size) * 100)}%`,
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
      },
      diagnomiqDonutCustom: {
        total,
        label: centerLabel,
        size,
      },
    },
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <Doughnut data={chartData} options={options} plugins={[donutPlugin]} />
      </div>

      <ul className="space-y-2 min-w-[160px]">
        {data.map((d) => {
          const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
          return (
            <li key={d.label} className="flex items-center gap-2.5">
              <span
                className="h-3 w-3 rounded-full shrink-0"
                style={{ backgroundColor: d.color }}
                aria-hidden="true"
              />
              <span className="text-sm text-[#10253A] flex-1">{d.label}</span>
              <span className="text-sm font-semibold text-[#10253A]">{d.value}</span>
              <span className="text-xs text-[#63788E] w-10 text-right">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PieChart;